import { useState, useEffect, useRef, useMemo } from 'react'
import { ChevronDownIcon, AlertCircleIcon } from './Icons'
import { Spinner } from './Spinner'

export interface ComboBoxOption {
  value: string
  label: string
  subtitle?: string
  disabled?: boolean
}

export interface ComboBoxProps {
  /** Label text displayed above the combobox */
  label: string
  /** Array of options to display in the dropdown */
  options: ComboBoxOption[]
  /** Current selected value (controlled) */
  value: string
  /** Callback when selection changes */
  onChange: (value: string) => void
  /** Placeholder text when no value is selected */
  placeholder?: string
  /** Placeholder text for search input */
  searchPlaceholder?: string
  /** Help text displayed below the combobox */
  helpText?: string
  /** Error message displayed below the combobox */
  error?: string | boolean
  /** Disabled state */
  disabled?: boolean
  /** Loading state */
  loading?: boolean
  /** Allow selecting empty value */
  allowEmpty?: boolean
  /** Label for empty option */
  emptyOptionLabel?: string
  /** Message shown when no results match search */
  noResultsMessage?: string
  /** Optional data-id for testing */
  'data-id'?: string
}

export function ComboBox({
  label,
  options,
  value,
  onChange,
  placeholder = 'Selecione uma opção',
  searchPlaceholder = 'Buscar...',
  helpText,
  error,
  disabled = false,
  loading = false,
  allowEmpty = false,
  emptyOptionLabel = 'Nenhum',
  noResultsMessage = 'Nenhum resultado encontrado',
  'data-id': dataId,
}: ComboBoxProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const optionsListRef = useRef<HTMLDivElement>(null)

  // Generate unique IDs for accessibility
  const comboboxId = `combobox-${label.replace(/\s+/g, '-').toLowerCase()}-${Math.random().toString(36).substr(2, 9)}`
  const helpTextId = `${comboboxId}-help`
  const errorTextId = `${comboboxId}-error`
  const listboxId = `${comboboxId}-listbox`
  const isError = !!error

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options
    const term = searchTerm.toLowerCase()
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        (option.subtitle && option.subtitle.toLowerCase().includes(term))
    )
  }, [options, searchTerm])

  // Build combined options list (empty option + filtered options)
  const allOptions = useMemo(() => {
    if (allowEmpty) {
      return [{ value: '', label: emptyOptionLabel }, ...filteredOptions]
    }
    return filteredOptions
  }, [allowEmpty, emptyOptionLabel, filteredOptions])

  // Get selected option display
  const selectedOption = useMemo(() => {
    if (!value) return null
    return options.find((option) => option.value === value)
  }, [value, options])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
        setHighlightedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(-1)
  }, [filteredOptions])

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsListRef.current) {
      const highlightedElement = optionsListRef.current.children[highlightedIndex] as HTMLElement
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [highlightedIndex])

  const handleToggle = () => {
    if (disabled || loading) return
    const newState = !isOpen
    setIsOpen(newState)
    if (!newState) {
      setSearchTerm('')
      setHighlightedIndex(-1)
    }
  }

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
    setSearchTerm('')
    setHighlightedIndex(-1)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled || loading) return

    switch (event.key) {
      case 'Enter':
        event.preventDefault()
        if (isOpen && highlightedIndex >= 0 && allOptions[highlightedIndex]) {
          handleSelect(allOptions[highlightedIndex].value)
        } else if (!isOpen) {
          setIsOpen(true)
        }
        break
      case 'Escape':
        event.preventDefault()
        setIsOpen(false)
        setSearchTerm('')
        setHighlightedIndex(-1)
        break
      case 'ArrowDown':
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        } else {
          setHighlightedIndex((prev) =>
            prev < allOptions.length - 1 ? prev + 1 : prev
          )
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (isOpen) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        }
        break
    }
  }

  // Border and focus styles matching Select exactly
  const baseBorderColor = error ? 'border-destructive' : 'border-border'
  const focusBorderColor = error ? 'border-destructive' : 'border-ring'

  // Wrapper classes matching Select
  const wrapperClasses = `
    relative flex items-stretch w-full rounded transition-all duration-150 ease-in-out border
    ${disabled ? 'bg-muted cursor-not-allowed' : 'bg-background'}
    ${isOpen ? `ring-1 ${error ? 'ring-destructive' : 'ring-ring'} ${focusBorderColor}` : baseBorderColor}
  `.trim()

  return (
    <div className="w-full font-sans" ref={containerRef}>
      {/* Label */}
      <label
        htmlFor={comboboxId}
        className="block text-[13px] font-medium text-foreground mb-1"
      >
        {label}
      </label>

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center py-3 bg-muted rounded border border-border">
          <Spinner size="small" />
          <span className="ml-2 text-sm text-muted-foreground">Carregando...</span>
        </div>
      ) : (
        <div className="relative">
          {/* Trigger button */}
          <div className={wrapperClasses}>
            <button
              type="button"
              id={comboboxId}
              role="combobox"
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              aria-controls={listboxId}
              aria-invalid={isError}
              aria-describedby={isError ? errorTextId : helpText ? helpTextId : undefined}
              onClick={handleToggle}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              className={`
                flex-1 w-full bg-transparent border-none outline-none text-left
                disabled:cursor-not-allowed disabled:text-muted-foreground
                px-3 py-2 pr-8 text-[14px] leading-5 min-h-[36px]
                focus:ring-0 focus:outline-none
              `.trim()}
              data-id={dataId}
            >
              {selectedOption ? (
                <div className="block">
                  <span className="font-medium text-foreground">{selectedOption.label}</span>
                  {selectedOption.subtitle && (
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {selectedOption.subtitle}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </button>

            {/* Chevron Icon */}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>

          {/* Dropdown */}
          {isOpen && (
            <div
              className="absolute z-50 mt-1 w-full bg-background border border-border rounded shadow-lg"
              role="presentation"
            >
              {/* Search input */}
              <input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border-b border-border text-sm focus:outline-none focus:ring-0 placeholder-muted-foreground"
                aria-label="Buscar opções"
              />

              {/* Options list */}
              <div
                ref={optionsListRef}
                role="listbox"
                id={listboxId}
                className="max-h-48 overflow-y-auto py-1"
              >
                {allOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">{noResultsMessage}</div>
                ) : (
                  allOptions.map((option, index) => {
                    const isSelected = value === option.value
                    const isHighlighted = highlightedIndex === index
                    const isEmptyOption = option.value === ''

                    return (
                      <button
                        key={option.value || 'empty-option'}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        disabled={option.disabled}
                        onClick={() => handleSelect(option.value)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        className={`
                          w-full text-left px-3 py-2 text-sm transition-colors
                          disabled:opacity-50 disabled:cursor-not-allowed
                          ${isSelected
                            ? 'bg-[#F1F8FF] text-blue-600 border-l-2 border-ring'
                            : isHighlighted
                              ? 'bg-accent text-foreground'
                              : 'text-foreground hover:bg-accent'
                          }
                          ${isEmptyOption ? 'text-muted-foreground' : ''}
                        `.trim()}
                      >
                        {isEmptyOption ? (
                          option.label
                        ) : (
                          <div className="flex flex-col">
                            <span className="font-medium">{option.label}</span>
                            {option.subtitle && (
                              <span className="text-xs text-muted-foreground mt-0.5">{option.subtitle}</span>
                            )}
                          </div>
                        )}
                      </button>
                    )
                  })
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help text or error */}
      {isError && typeof error === 'string' ? (
        <div id={errorTextId} className="mt-1 flex items-center gap-1.5 text-destructive text-xs">
          <AlertCircleIcon className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : helpText ? (
        <div id={helpTextId} className="mt-1 text-muted-foreground text-xs">
          {helpText}
        </div>
      ) : null}
    </div>
  )
}
